import {useI18n} from "vue-i18n";
import {SupportedLang} from "@/i18n.ts";
import {StorageService} from "@/services/storageService.ts";
import {useSettingsStore} from "@/stores/useSettingsStore.ts";
import {UserPreferences} from "@/models/settings.ts";
import {computed, ComputedRef} from "vue";

export type LocaleUseCases = {
    [K in SupportedLang]: string; // {he: string, enUS: string}
};

export function useLocale() {
    const { t, locale, availableLocales } = useI18n()
    const settingsStore = useSettingsStore();

    const direction = (nextLocale: SupportedLang) => {
        return nextLocale === 'he' ? 'rtl' : 'ltr';
    }

    const changeLocale = (nextLocale: SupportedLang) => {
        // runtime validation
        if (availableLocales.some(l => l === nextLocale)) {
            locale.value = nextLocale;
            document.documentElement.lang = nextLocale;
            document.documentElement.dir = direction(nextLocale);
        }

    }

    const initLocale = async () => {
        const preferences = await StorageService.Get<UserPreferences>("USER_PREFERENCES")
        if (preferences?.defaultLanguage) {
            changeLocale(preferences.defaultLanguage)
        } else if (settingsStore?.suiteSettings?.defaultLanguage) {
            changeLocale(settingsStore.suiteSettings.defaultLanguage as SupportedLang)
        }
    }

    const tUseCase = (obj: LocaleUseCases, params?: Record<string, any>) => {
        const translation = obj[locale.value as SupportedLang]
        for (const key in params) {
            translation.replace(`{${key}}`, params[key])
        }
        return translation
    }

    const localeTwoLetterCode: ComputedRef<string> = computed(() => {
        switch (locale.value) {
            case "enUS": return "en-US"
            case "he": return "he"
            default: return "he"
        }
    })

    return {
        changeLocale,
        initLocale,
        locale,
        localeTwoLetterCode,
        t,
        tUseCase
    };
}